import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { People } from '@/hooks/usePeople'
import { checklistProgress, dateLine, fmtShort, kindLabel } from '@/logic/labels'
import type { PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useUi } from '@/store/ui'
import type { Category, DateStr } from '@/types'
import { Avatar, PairAvatars } from './Avatar'
import { LinkedText } from './LinkedText'
import { StatusRing } from './StatusRing'
import { AlertIcon, BellIcon, CheckIcon, ChecklistIcon, ChevronDownIcon, LockIcon, RepeatIcon, RoutineIcon } from './icons'
import './TaskCard.css'

export interface CardWhere {
  /** The day the card sits on; the details line drops that date. */
  day?: DateStr | null
  /** In the tray's Not done group: "Overdue, Thu 8 Oct", "Didn't do it, Mon 12". */
  notDone?: boolean
}

export interface TaskCardProps {
  item: PlanItem
  where: CardWhere
  now: Now
  category: Category | null
  people: People
  /** A past week: the card opens read-only and the ring is not a control. */
  readOnly?: boolean
  /** Done rows in "Done earlier this week" show the day they were ticked. */
  showDoneDay?: boolean
  onOpen: () => void
  onToggleDone: () => void
  onToggleStep?: (stepId: string, done: boolean) => void
  /** In Select mode: a selection circle stands in for the ring, and the card's tap picks it. */
  selection?: { selected: boolean; onToggle: () => void }
}

/**
 * One task as a list row: the person at the left, the title up to two lines,
 * and the ring at the right, under the thumb; under the title the description's first line in muted text,
 * with a small chevron that unfolds the rest in place; then a details line across
 * the full width with the checklist button at its right end (on the description
 * line instead when there are no details, so no row stands nearly empty). A
 * ticked task shrinks to one struck line; a preview in a later week is drawn at
 * half strength.
 */
export function TaskCard(props: TaskCardProps) {
  const { item, where, now, category, people, readOnly = false, showDoneDay = false, onOpen, onToggleDone, onToggleStep, selection } = props
  const t = item.task
  const [descOpen, setDescOpen] = useState(false)
  const [stepsOpen, setStepsOpen] = useState(false)
  const [descClipped, setDescClipped] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const descLine = useRef<HTMLSpanElement>(null)
  const highlight = useUi((s) => (s.highlight?.id === t.id ? s.highlight.nonce : null))
  const color = category ? `var(--${category.color})` : null

  // the sheen: replayed whenever this task is the one just added or shown
  useEffect(() => {
    const el = ref.current
    if (!highlight || !el) return
    el.classList.remove('card--sheen')
    void el.offsetWidth
    el.classList.add('card--sheen')
    const timer = window.setTimeout(() => el.classList.remove('card--sheen'), 1600)
    return () => window.clearTimeout(timer)
  }, [highlight])

  // the chevron only when there is more than the one line shows: cut short, or more lines after it
  const description = t.description ?? ''
  useLayoutEffect(() => {
    const el = descLine.current
    if (!el || descOpen) return
    const measure = () => setDescClipped(el.scrollWidth > el.clientWidth + 1 || description.trim().includes('\n'))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [description, descOpen])

  if (item.done) {
    // every tick comes off, a past week's too: a one-off reopens, an occurrence leaves the history
    return (
      <div ref={ref} className="card card--done" data-task-id={t.id}>
        <button type="button" className="card__open" aria-label={`Open ${t.title}`} onClick={onOpen} />
        <div className="card__done-row">
          <span className="card__done-title">{t.title}</span>
          {showDoneDay && item.doneOn && <span className="card__done-day">{fmtShort(item.doneOn).split(' ')[0]}</span>}
          <StatusRing state="done" color={color} size="small" label={`Reopen ${t.title}`} onToggle={onToggleDone} />
        </div>
      </div>
    )
  }

  const inert = readOnly || item.faint
  const line = item.faint ? null : dateLine(t, where, now)
  const kind = kindLabel(t)
  const steps = t.checklist ?? []
  const hasSteps = steps.length > 0 && !item.faint
  const hasDesc = !!t.description && t.description.trim() !== '' && !item.faint
  const reminds = !!t.reminder && !!t.dueTime && where.day === t.dueDate
  const isPrivate = t.scope === 'private'
  const assignee = isPrivate ? null : (t.assigneeUid ?? null)
  const hasDetails = t.waiting || !!line || !!kind || !!category
  // with no details line, the checklist button joins the description line, or with no description
  // either the title's line, beside the ring, so no row stands nearly empty
  const stepsOnDescLine = hasSteps && hasDesc && !hasDetails
  const stepsOnTitleLine = hasSteps && !hasDesc && !hasDetails
  const stepsTool = hasSteps && (
    <button
      type="button"
      className={`card__tool ${stepsOpen ? 'card__tool--on' : ''}`}
      aria-label={`${stepsOpen ? 'Hide' : 'Show'} checklist of ${t.title}`}
      aria-expanded={stepsOpen}
      onClick={() => setStepsOpen((v) => !v)}
    >
      <span className="card__tool-box card__tool-box--steps">
        <ChecklistIcon size={13} />
        {checklistProgress(t)}
      </span>
    </button>
  )

  return (
    <div ref={ref} className={`card ${item.faint ? 'card--faint' : ''} ${t.pending ? 'card--pending' : ''} ${selection?.selected ? 'card--selected' : ''}`} data-task-id={t.id}>
      {selection ? (
        <button type="button" className="card__open" aria-label={`${selection.selected ? 'Unselect' : 'Select'} ${t.title}`} aria-pressed={selection.selected} onClick={selection.onToggle} />
      ) : (
        <button type="button" className="card__open" aria-label={`Open ${t.title}`} onClick={onOpen} />
      )}
      <div className="card__grid">
        <span className="card__who">
          {isPrivate && <LockIcon size={16} title="Private" className="card__lock" />}
          {!isPrivate && assignee && <Avatar uid={assignee} people={people} label={`For ${people.names[assignee] ?? 'someone'}`} />}
          {!isPrivate && !assignee && <PairAvatars people={people} />}
        </span>
        <div className="card__title">{t.title}</div>
        <span className="card__end">
          {stepsOnTitleLine && <span className="card__tools">{stepsTool}</span>}
          {selection ? (
            <span className="ring-slot ring-slot--card card__select-slot" aria-hidden="true">
              <span className={`card__select ${selection.selected ? 'card__select--on' : ''}`}>{selection.selected && <CheckIcon size={13} weight={3.4} />}</span>
            </span>
          ) : (
            <StatusRing state={t.waiting ? 'waiting' : 'open'} color={color} label={inert ? (item.faint ? `Not yet: ${t.title}` : t.title) : `Complete ${t.title}`} inert={inert} onToggle={onToggleDone} />
          )}
        </span>
        {hasDesc && (
          <div className={`card__descline ${descOpen ? 'card__descline--open' : ''}`}>
            <span ref={descLine} className="card__desc-text">
              <LinkedText text={descOpen ? description : description.trim()} links={descOpen} />
            </span>
            {(descClipped || descOpen) && (
              <button
                type="button"
                className={`card__expand ${descOpen ? 'card__expand--open' : ''}`}
                aria-label={`${descOpen ? 'Hide' : 'Show'} description of ${t.title}`}
                aria-expanded={descOpen}
                onClick={() => setDescOpen((v) => !v)}
              >
                <ChevronDownIcon size={14} weight={2.4} />
              </button>
            )}
            {stepsOnDescLine && <span className="card__tools">{stepsTool}</span>}
          </div>
        )}
        {hasDetails && (
          <div className="card__meta">
            <div className="card__details">
              {t.waiting && <span className="card__waiting">Waiting</span>}
              {line && (
                <span className={`card__date ${line.tone ? `card__date--${line.tone}` : ''}`}>
                  {line.tone === 'late' && <AlertIcon size={13} />}
                  {reminds && line.tone !== 'late' && <BellIcon size={13} title="Reminder set" />}
                  {line.text}
                </span>
              )}
              {kind && (
                <span className="card__kind">
                  {t.kind === 'routine' ? <RoutineIcon size={13} /> : <RepeatIcon size={13} />}
                  {kind}
                </span>
              )}
              {category && <span>{category.name}</span>}
            </div>
            {hasSteps && <span className="card__tools">{stepsTool}</span>}
          </div>
        )}
      </div>
      {stepsOpen && hasSteps && (
        <div className="card__steps">
          {steps.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`card__step ${s.done ? 'card__step--done' : ''}`}
              aria-pressed={s.done}
              disabled={readOnly}
              onClick={() => onToggleStep?.(s.id, !s.done)}
            >
              <span className="card__step-box">{s.done && <CheckIcon size={12} weight={3.6} />}</span>
              <span className="card__step-text">{s.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
